import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Search, Lock, AlertTriangle, KeyRound, UserCheck, UserX, Calendar, X } from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { Panel, AdminSelect, EmptyState } from "@/admin/components/ui";
import SEO from "@/components/SEO";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

const TYPE_INFO = {
  login_success:                { label: "Login OK",          tone: "text-[#7fe6ff] border-[#7fe6ff]/40", icon: UserCheck },
  login_failed:                 { label: "Failed Login",      tone: "text-[#ff9fcf] border-[#ff9fcf]/40", icon: UserX },
  login_blocked_locked:         { label: "Account Locked",    tone: "text-[#ffd479] border-[#ffd479]/40", icon: Lock },
  password_reset_requested:     { label: "Reset Requested",   tone: "text-[#18d3ff] border-[#18d3ff]/40", icon: KeyRound },
  password_reset_requested_unknown: { label: "Reset · Unknown email", tone: "text-white/55 border-white/15", icon: KeyRound },
  password_reset_completed:     { label: "Reset Completed",   tone: "text-[#7fe6ff] border-[#7fe6ff]/40", icon: ShieldCheck },
  password_reset_invalid:       { label: "Invalid Reset",     tone: "text-[#ff9fcf] border-[#ff9fcf]/40", icon: AlertTriangle },
  password_reset_expired:       { label: "Expired Reset",     tone: "text-[#ffd479] border-[#ffd479]/40", icon: AlertTriangle },
};

const TypeBadge = ({ type }) => {
  const o = TYPE_INFO[type] || { label: type, tone: "text-white/55 border-white/15", icon: AlertTriangle };
  const Icon = o.icon;
  return (
    <span data-testid={`sec-evt-badge-${type}`} className={`inline-flex items-center gap-1 text-[10px] tracking-luxe uppercase px-2 py-0.5 rounded-full glass-strong border ${o.tone}`}>
      <Icon className="w-3 h-3" /> {o.label}
    </span>
  );
};

export default function AdminSecurityEvents() {
  const [data, setData] = useState({ events: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("all");
  const [roleF, setRoleF] = useState("all");
  const [drawer, setDrawer] = useState(null);

  useEffect(() => {
    setLoading(true);
    adminApi.get("/admin/security-events")
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatErr(err)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => (data.events || []).filter((r) => {
    if (typeF !== "all" && r.type !== typeF) return false;
    if (roleF !== "all" && r.role !== roleF) return false;
    if (q) {
      const blob = `${r.email_masked || ""} ${r.ip_address || ""} ${r.detail || ""}`.toLowerCase();
      if (!blob.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [data.events, typeF, roleF, q]);

  const c = data.counts || {};

  return (
    <div data-testid="page-admin-security-events" className="space-y-6">
      <SEO title="Security Events · WelkomBiz Photography" noindex />
      <Panel testId="sec-evt-explainer">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-4 h-4 text-[#ff2e93] mt-0.5 shrink-0" />
          <p className="text-sm text-white/65 font-body">
            Read-only audit feed for authentication and password-reset activity. Email addresses are stored masked
            (never raw). No tokens, password hashes or signed URLs are recorded here.
          </p>
        </div>
      </Panel>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div data-testid="sec-evt-total" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Total</p><p className="font-display text-2xl text-chrome">{c.total || 0}</p></div>
        <div data-testid="sec-evt-failed" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Failed Logins</p><p className="font-display text-2xl text-[#ff9fcf]">{c.login_failed || 0}</p></div>
        <div data-testid="sec-evt-locked" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Lockouts</p><p className="font-display text-2xl text-[#ffd479]">{c.login_blocked_locked || 0}</p></div>
        <div data-testid="sec-evt-success" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Successful</p><p className="font-display text-2xl text-[#7fe6ff]">{c.login_success || 0}</p></div>
        <div data-testid="sec-evt-reset-req" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Reset Requests</p><p className="font-display text-2xl text-[#18d3ff]">{c.password_reset_requested || 0}</p></div>
        <div data-testid="sec-evt-reset-done" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Reset Completed</p><p className="font-display text-2xl text-[#7fe6ff]">{c.password_reset_completed || 0}</p></div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex items-center gap-3 flex-1 glass chrome-border rounded-full px-4 py-2">
          <Search className="w-4 h-4 text-white/45" />
          <input data-testid="sec-evt-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by masked email, IP, detail…" className="flex-1 bg-transparent text-sm text-white placeholder-white/35 outline-none font-body" />
        </div>
        <AdminSelect data-testid="sec-evt-filter-type" value={typeF} onChange={(e) => setTypeF(e.target.value)} className="sm:w-56">
          <option value="all" className="bg-black">All event types</option>
          {Object.keys(TYPE_INFO).map((t) => <option key={t} value={t} className="bg-black">{TYPE_INFO[t].label}</option>)}
        </AdminSelect>
        <AdminSelect data-testid="sec-evt-filter-role" value={roleF} onChange={(e) => setRoleF(e.target.value)} className="sm:w-36">
          <option value="all" className="bg-black">All roles</option>
          <option value="admin" className="bg-black">Admin</option>
          <option value="customer" className="bg-black">Customer</option>
          <option value="unknown" className="bg-black">Unknown</option>
        </AdminSelect>
      </div>

      <Panel className="!p-0 overflow-hidden" testId="sec-evt-table">
        {loading ? <p className="p-6 text-sm text-white/40">Loading…</p> :
         filtered.length === 0 ? <div className="p-6"><EmptyState title="No security events yet" message="Login attempts and password-reset activity will appear here as the system is used." /></div> :
         (<>
           <div className="hidden md:block overflow-x-auto">
             <table className="w-full text-sm">
               <thead className="bg-white/3 text-[10px] tracking-luxe uppercase text-white/45">
                 <tr>
                   <th className="text-left px-5 py-3">When</th>
                   <th className="text-left px-5 py-3">Type</th>
                   <th className="text-left px-5 py-3">Role</th>
                   <th className="text-left px-5 py-3">Email</th>
                   <th className="text-left px-5 py-3">IP</th>
                   <th className="text-right px-5 py-3">Action</th>
                 </tr>
               </thead>
               <tbody>
                 {filtered.map((r) => (
                   <tr key={r.id} data-testid={`sec-evt-row-${r.id}`} className="border-t border-white/5 hover:bg-white/3">
                     <td className="px-5 py-3 text-white/70 whitespace-nowrap"><Calendar className="w-3 h-3 inline mr-1 text-white/40" />{fmtDate(r.created_at)}</td>
                     <td className="px-5 py-3"><TypeBadge type={r.type} /></td>
                     <td className="px-5 py-3 text-white/70 text-[11px] tracking-luxe uppercase">{r.role || "—"}</td>
                     <td className="px-5 py-3 text-white/85">{r.email_masked || "—"}</td>
                     <td className="px-5 py-3 text-white/45 whitespace-nowrap">{r.ip_address || "—"}</td>
                     <td className="px-5 py-3 text-right">
                       <button onClick={() => setDrawer(r)} data-testid={`sec-evt-view-${r.id}`} className="text-[10px] tracking-luxe uppercase text-white/70 hover:text-white">Details →</button>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>
           <div className="md:hidden divide-y divide-white/5">
             {filtered.map((r) => (
               <button key={r.id} data-testid={`sec-evt-card-${r.id}`} onClick={() => setDrawer(r)} className="block w-full text-left p-4">
                 <div className="flex items-start justify-between gap-3">
                   <div className="min-w-0">
                     <p className="text-sm text-white truncate">{r.email_masked || "—"}</p>
                     <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-0.5">{fmtDate(r.created_at)} · {r.role || "—"}</p>
                   </div>
                   <TypeBadge type={r.type} />
                 </div>
               </button>
             ))}
           </div>
         </>)}
      </Panel>

      {drawer && (
        <>
          <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50" onClick={() => setDrawer(null)} data-testid="sec-evt-detail-overlay" />
          <aside data-testid="sec-evt-detail-drawer" className="fixed top-0 right-0 bottom-0 w-full sm:w-[480px] z-50 glass-strong border-l border-white/10 overflow-y-auto">
            <header className="px-5 py-4 border-b border-white/5 flex items-center gap-3 sticky top-0 glass-strong">
              <ShieldCheck className="w-4 h-4 text-[#ff2e93]" />
              <h2 className="font-display text-lg text-chrome flex-1">Security Event</h2>
              <button onClick={() => setDrawer(null)} data-testid="sec-evt-detail-close" className="w-8 h-8 rounded-full border border-white/10 text-white/70 hover:text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
            </header>
            <div className="px-5 py-5 space-y-3 text-sm">
              <Panel>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><p className="text-[10px] tracking-luxe uppercase text-white/45">Type</p><TypeBadge type={drawer.type} /></div>
                  <div><p className="text-[10px] tracking-luxe uppercase text-white/45">Role</p><p className="text-white">{drawer.role || "—"}</p></div>
                  <div><p className="text-[10px] tracking-luxe uppercase text-white/45">When</p><p className="text-white">{fmtDate(drawer.created_at)}</p></div>
                  <div><p className="text-[10px] tracking-luxe uppercase text-white/45">IP</p><p className="text-white">{drawer.ip_address || "—"}</p></div>
                  <div className="col-span-2"><p className="text-[10px] tracking-luxe uppercase text-white/45">Email</p><p className="text-white">{drawer.email_masked || "—"}</p></div>
                  <div className="col-span-2"><p className="text-[10px] tracking-luxe uppercase text-white/45">User-Agent</p><p className="text-xs text-white/65 break-words">{drawer.user_agent || "—"}</p></div>
                  {drawer.detail && <div className="col-span-2"><p className="text-[10px] tracking-luxe uppercase text-white/45">Detail</p><p className="text-xs text-white/65">{drawer.detail}</p></div>}
                </div>
              </Panel>
              <div className="text-[10px] tracking-luxe uppercase text-white/40 inline-flex items-center gap-2">
                <ShieldCheck className="w-3 h-3 text-[#ff2e93]" />
                No raw emails, passwords or tokens are ever stored here.
              </div>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
